<template>
	<div class="w-full">
		<div class="mb-3">
			<span class="mb-0 font-medium text-gray-650">Make and model</span>
		</div>

		<div v-if="isDirty" class="m-0 space-y-3 p-0">
			<FilterSelectbox
				ref="makeOptionsSelect"
				v-model="makes"
				mode="multiple"
				:options="makeOptionsGrouped"
				value-prop="value"
				label="label"
				placeholder="Make"
				:searchable="true"
				:groups="true"
				:group-select="false"
				:close-on-select="false"
				:hide-selected="false"
				:show-apply-and-clear="true"
				:can-clear="false"
				:can-deselect="false"
				@apply="onApply($event, 'makes')"
				@close="onApply($event, 'makes')"
				@clear="onClear('makes')"
			></FilterSelectbox>

			<FilterSelectbox
				ref="modelOptionsSelect"
				v-model="models"
				mode="multiple"
				:options="modelOptions"
				value-prop="value"
				label="label"
				placeholder="Model"
				:searchable="true"
				:disabled="modelFilterDisabled"
				:groups="false"
				:group-select="false"
				:close-on-select="false"
				:hide-selected="false"
				:show-apply-and-clear="true"
				:can-clear="false"
				:can-deselect="false"
				@apply="onApply($event, 'models')"
				@close="onApply($event, 'models')"
				@clear="onClear('models')"
			></FilterSelectbox>
		</div>

		<div v-if="showContentLoader" class="flex flex-col gap-2">
			<ContentLoader width="w-full" height="h-[50px]" />
			<ContentLoader width="w-full" height="h-[50px]" />
		</div>
	</div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import FilterSelectbox from '@/components/selectboxes/FilterSelectbox.vue'
import { useProductCatalog } from '@/stores/product-catalog'
import ContentLoader from '@/components/ContentLoader.vue'

const productCatalog = useProductCatalog()

const makes = ref([])
const models = ref([])
const modelFilterDisabled = computed(() => makes.value.includes('all'))

const makeOptionsSelect = ref(null)
const modelOptionsSelect = ref(null)

const makeOptions = computed(() => [
	{
		label: 'All makes',
		value: 'all',
	},
	...productCatalog.facets.makes,
])

const makeOptionsGrouped = computed(() => {
	const options = []

	const popularMakesOptions = [
		...productCatalog.facets.makes.filter((make) => make.popular),
	]

	if (popularMakesOptions.length) {
		options.push({
			label: 'Popular makes',
			options: popularMakesOptions,
		})
	}

	options.push({
		label: 'All makes',
		options: makeOptions,
	})

	return options
})

const modelOptions = computed(() => [
	{
		label: 'All models',
		value: 'all',
	},
	...productCatalog.facets.models,
])

const isDirty = computed(() => productCatalog.isDirty)

const showContentLoader = computed(() => !isDirty.value)

watch(isDirty, (newValue) => {
	if (newValue) {
		makes.value = productCatalog.filters.makes.length
			? productCatalog.filters.makes
			: ['all']
		models.value = productCatalog.filters.models.length
			? productCatalog.filters.models
			: ['all']
	}
})

watch(makeOptions, () => {
	makeOptionsSelect.value.refreshLabels()
})

watch(modelOptions, () => {
	modelOptionsSelect.value.refreshLabels()
})

watch(makes, (newValue, oldValue) => {
	handleAllSelection(newValue, oldValue, makes)
})

watch(models, (newValue, oldValue) => {
	models.value = handleAllSelection(newValue, oldValue, models)
})

watch(
	() => productCatalog.filters.makes,
	(newValue) => {
		makes.value = newValue.length === 0 ? ['all'] : newValue
	}
)

watch(
	() => productCatalog.filters.models,
	(newValue) => {
		models.value = newValue.length === 0 ? ['all'] : newValue
	}
)

const handleAllSelection = (newValue, oldValue, facet) => {
	let { value } = facet

	// when adding the 'all' option, remove all other selections
	if (
		newValue.length > 1 &&
		!oldValue.some((item) => `${item}`.toLowerCase() === 'all') &&
		newValue.some((item) => `${item}`.toLowerCase() === 'all')
	) {
		value = ['all']
	}

	// when adding an option while the 'all' option is selected,
	// remove the 'all' option
	if (
		newValue.length > 1 &&
		oldValue.some((item) => `${item}`.toLowerCase() === 'all') &&
		newValue.some((item) => `${item}`.toLowerCase() === 'all')
	) {
		const index = value.indexOf('all')
		if (index > -1) {
			value.splice(index, 1)
		}
	}

	return value
}

const onApply = (filterValue, filterKey) => {
	let updateFilterValue = filterValue

	if (updateFilterValue.some((item) => `${item}`.toLowerCase() === 'all')) {
		updateFilterValue = ['all']
	}

	updateFilter(updateFilterValue, filterKey)
}

const onClear = (filterKey) => {
	updateFilter(['all'], filterKey)
}

const updateFilter = (filterValue, filterKey) => {
	let updateFilterValue = filterValue

	// handle when 'all' is selected
	if (updateFilterValue.some((item) => `${item}`.toLowerCase() === 'all')) {
		updateFilterValue = []
	}

	productCatalog.updateFilter({
		filterKey,
		filterValue: updateFilterValue,
	})
}
</script>
